import { Map as MapLibreMap, Popup, type GeoJSONSource, type MapLayerMouseEvent } from 'maplibre-gl';
import { useEffect, useRef, useState } from 'react';
import type { ColorMode } from '@chukua/common';
import { configureMapLibreWorker } from '../../../../../shared/features/maps/adapters/configure-maplibre-worker';
import type { ZoneKind } from '../../../../reference-data/reference-data.api';
import type { LngLatBounds } from '../policies/zone-map-bounds';
import { basemapStyleUrl } from '../../../../../shared/features/maps/policies/basemap';
import { addCompanyMarker, type CompanyOnMap } from '../adapters/company-marker';
import { CORRIDOR_CENTER, CORRIDOR_ZOOM, ZONE_FIT_OPTIONS } from '../../../../../shared/features/maps/policies/map-framing';
import type { Zone } from '../../../zones.api';
import { zonesMapSources } from '../policies/zones-map-data';
import {
  KIND_COLORED_LAYERS,
  ZONE_CLUSTER_LAYER_ID,
  ZONE_EDIT_RADIUS_SOURCE_ID,
  ZONE_HIT_LAYER_IDS,
  ZONE_POINTS_SOURCE,
  ZONE_POINTS_SOURCE_ID,
  ZONE_SHAPES_SOURCE_ID,
  editRadiusLayers,
  zoneColorExpression,
  zoneOpacityPaint,
  zoneLayers,
} from '../adapters/zones-map-layers';

/** Where the map should look: refitted each time `key` changes, so a reload of the same results leaves the view alone. */
export interface MapFrame {
  key: string;
  bounds: LngLatBounds | null;
}

interface UseZonesMapArgs {
  /** Marked on the map, and where it opens before any zones frame it; null until the company's location is set. */
  company: CompanyOnMap | null;
  /** The app's colour mode: the basemap and every overlay follow it, and the map is rebuilt when it changes. */
  mode: ColorMode;
  /** Null while unknown or unset: no map is mounted. */
  tileKey: string | null;
  zones: Zone[];
  zoneKinds: ZoneKind[];
  /** The zone whose panel is open: left out of the overview (terra-draw draws it) and unclickable around it. */
  editingZoneId: string | null;
  frame: MapFrame;
  onSelect: (zoneId: string) => void;
}

function zoneAt(event: MapLayerMouseEvent): { zoneId: string; name: string } | null {
  const properties = event.features?.[0]?.properties;
  if (typeof properties?.zoneId !== 'string') return null;
  return { zoneId: properties.zoneId, name: String(properties.name) };
}

interface MountArgs {
  mode: ColorMode;
  center: [number, number];
  container: HTMLDivElement;
  tileKey: string;
  /** Reads the latest kinds and selection handler, not the ones at mount. */
  current: () => { zoneKinds: ZoneKind[]; isEditing: boolean; onSelect: (zoneId: string) => void };
  onLoad: (map: MapLibreMap) => void;
}

/** A cluster zooms in far enough to split on click. While a zone is being drawn the click is terra-draw's, as it is for single zones. */
function wireClusters(map: MapLibreMap, isEditing: () => boolean) {
  map.on('mouseenter', ZONE_CLUSTER_LAYER_ID, () => {
    if (!isEditing()) map.getCanvas().style.cursor = 'pointer';
  });
  map.on('mouseleave', ZONE_CLUSTER_LAYER_ID, () => {
    map.getCanvas().style.cursor = '';
  });
  map.on('click', ZONE_CLUSTER_LAYER_ID, (event) => {
    const cluster = event.features?.[0];
    const clusterId = cluster?.properties?.cluster_id;
    if (isEditing() || typeof clusterId !== 'number' || cluster?.geometry.type !== 'Point') return;
    const center = cluster.geometry.coordinates as [number, number];
    void map
      .getSource<GeoJSONSource>(ZONE_POINTS_SOURCE_ID)
      ?.getClusterExpansionZoom(clusterId)
      .then((zoom) => map.easeTo({ center, zoom }));
  });
}

/** The map, its zones sources and layers, a hover label naming the zone, and click-to-open while no panel is open. */
function mountZonesMap({ container, tileKey, mode, center, current, onLoad }: MountArgs) {
  configureMapLibreWorker();
  const map = new MapLibreMap({
    container,
    style: basemapStyleUrl(mode, tileKey),
    center,
    zoom: CORRIDOR_ZOOM,
  });
  // Styled from the app's palette in styles.css (.zone-map-label), so it reads in either colour mode.
  const label = new Popup({ closeButton: false, closeOnClick: false, offset: 10, className: 'zone-map-label' });

  map.on('load', () => {
    const empty = zonesMapSources([]);
    map.addSource(ZONE_SHAPES_SOURCE_ID, { type: 'geojson', data: empty.shapes });
    map.addSource(ZONE_POINTS_SOURCE_ID, { ...ZONE_POINTS_SOURCE, data: empty.points });
    map.addSource(ZONE_EDIT_RADIUS_SOURCE_ID, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    [...zoneLayers(current().zoneKinds, { mode, isDimmed: current().isEditing }), ...editRadiusLayers(mode)].forEach((layer) => map.addLayer(layer));
    onLoad(map);
  });
  map.on('mousemove', ZONE_HIT_LAYER_IDS, (event) => {
    const zone = zoneAt(event);
    if (!zone) return;
    if (!current().isEditing) map.getCanvas().style.cursor = 'pointer';
    label.setLngLat(event.lngLat).setText(zone.name).addTo(map);
  });
  map.on('mouseleave', ZONE_HIT_LAYER_IDS, () => {
    map.getCanvas().style.cursor = '';
    label.remove();
  });
  // While a zone is being drawn, a click belongs to terra-draw, not to opening another zone.
  map.on('click', ZONE_HIT_LAYER_IDS, (event) => {
    const zone = zoneAt(event);
    if (zone && !current().isEditing) current().onSelect(zone.zoneId);
  });

  wireClusters(map, () => current().isEditing);

  const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => map.resize());
  resizeObserver?.observe(container);

  return () => {
    resizeObserver?.disconnect();
    map.remove();
  };
}

interface ZonePaintArgs {
  map: MapLibreMap | null;
  zoneKinds: ZoneKind[];
  mode: ColorMode;
  isDimmed: boolean;
}

/** Repaints the zones on the live map: their kind colours as the catalogue arrives, and dimmed while one zone is open. */
function useZonePaint({ map, zoneKinds, mode, isDimmed }: ZonePaintArgs) {
  useEffect(() => {
    if (!map) return;
    const color = zoneColorExpression(zoneKinds, mode);
    KIND_COLORED_LAYERS.forEach(({ id, property }) => map.setPaintProperty(id, property, color));
  }, [map, zoneKinds, mode]);

  useEffect(() => {
    if (!map) return;
    zoneOpacityPaint(isDimmed).forEach(({ layerId, property, value }) => map.setPaintProperty(layerId, property, value));
  }, [map, isDimmed]);
}

/**
 * The Zones map: every zone the search and filters match, coloured by kind. Returns the map once it has
 * loaded, so the drawing hook can put terra-draw on the same map for the zone being edited.
 */
export function useZonesMap({ tileKey, mode, company, zones, zoneKinds, editingZoneId, frame, onSelect }: UseZonesMapArgs) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [map, setMap] = useState<MapLibreMap | null>(null);
  const latestRef = useRef({ zoneKinds, isEditing: editingZoneId !== null, onSelect, bounds: frame.bounds, company });

  useEffect(() => {
    latestRef.current = { zoneKinds, isEditing: editingZoneId !== null, onSelect, bounds: frame.bounds, company };
  });

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !tileKey) return;
    const { company: home } = latestRef.current;
    const center: [number, number] = home ? [home.location.lng, home.location.lat] : CORRIDOR_CENTER;
    const destroy = mountZonesMap({ container, tileKey, mode, center, current: () => latestRef.current, onLoad: setMap });
    return () => {
      setMap(null);
      destroy();
    };
  }, [tileKey, mode]);

  useEffect(() => {
    const others = zones.filter((zone) => zone.id !== editingZoneId);
    const { shapes, points } = zonesMapSources(others);
    map?.getSource<GeoJSONSource>(ZONE_SHAPES_SOURCE_ID)?.setData(shapes);
    map?.getSource<GeoJSONSource>(ZONE_POINTS_SOURCE_ID)?.setData(points);
  }, [map, zones, editingZoneId]);

  useZonePaint({ map, zoneKinds, mode, isDimmed: editingZoneId !== null });

  useEffect(() => {
    if (!map || !company) return;
    return addCompanyMarker(map, company, mode);
  }, [map, company, mode]);

  const frameKey = frame.key;
  useEffect(() => {
    const { bounds } = latestRef.current;
    if (map && bounds) map.fitBounds(bounds, { ...ZONE_FIT_OPTIONS, duration: 0 });
  }, [map, frameKey]);

  return { containerRef, map };
}
